<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useKeyboardClose } from './useKeyboardClose';

const props = withDefaults(
  defineProps<{
    modalName: string;
    message: string;
    header?: string | null;
    severity?: 'alert' | 'warning' | 'info' | 'custom';
    btnOk?: string;
    btnCancel?: string;
    default?: 'ok' | 'cancel';
  }>(),
  {
    header: null,
    severity: 'alert',
    btnOk: 'OK',
    btnCancel: 'Cancel',
    default: 'cancel',
  },
);

const emit = defineEmits<{
  (event: 'ok', payload: { ok: true; value: true; modalName: string }): void;
  (
    event: 'cancel',
    payload: { cancel: true; value: false; modalName: string },
  ): void;
  (event: 'close'): void;
}>();

const isVisible = ref(true);
const containerRef = ref<HTMLElement | null>(null);
const btnOkRef = ref<HTMLButtonElement | null>(null);
const btnCancelRef = ref<HTMLButtonElement | null>(null);

const severityIcon = computed(() => {
  switch (props.severity) {
    case 'alert':
      return 'mdi:alert';
    case 'warning':
      return 'material-symbols:warning-rounded';
    case 'info':
      return 'material-symbols:info';
    default:
      return '';
  }
});
const severityIconClass = computed(() => {
  switch (props.severity) {
    case 'alert':
      return 'text-danger';
    case 'warning':
      return 'text-warning';
    case 'info':
      return 'text-info';
    default:
      return '';
  }
});
const severityClass = computed(() => `severity-${props.severity}`);

function closeAll() {
  isVisible.value = false;
  emit('close');
}

function ok() {
  isVisible.value = false;
  emit('ok', { ok: true, value: true, modalName: props.modalName });
}
function cancel() {
  isVisible.value = false;
  emit('cancel', { cancel: true, value: false, modalName: props.modalName });
}

useKeyboardClose({
  container: () => containerRef.value,
  onEscape: () => cancel(),
});

onMounted(() => {
  if (props.default === 'ok') {
    btnOkRef.value?.focus?.();
  } else if (props.default === 'cancel') {
    btnCancelRef.value?.focus?.();
  } else {
    containerRef.value?.focus?.();
  }
});
</script>

<template>
  <div v-if="isVisible" class="modal-in-portal">
    <div
      ref="containerRef"
      class="modal show d-block"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="modal-dialog modal-dialog-centered confirm-dialog"
        :class="severityClass"
      >
        <div class="modal-content">
          <div class="modal-body px-4 pt-4 pb-2">
            <div class="d-flex align-items-center">
              <div class="p-2 pe-4" :class="severityIconClass">
                <iconify-icon
                  v-if="severityIcon"
                  :icon="severityIcon"
                  style="font-size: 4rem"
                />
              </div>

              <div class="d-flex flex-column">
                <h5 v-if="header" class="pb-3">{{ header }}</h5>
                <p class="mb-0">{{ message }}</p>
              </div>
            </div>
          </div>

          <div class="modal-footer bg-light text-dark">
            <button
              ref="btnOkRef"
              type="button"
              class="btn btn-primary"
              @click.prevent.stop="ok"
            >
              {{ btnOk }}
            </button>
            <button
              ref="btnCancelRef"
              type="button"
              class="btn"
              @click.prevent.stop="cancel"
            >
              {{ btnCancel }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-backdrop show" />
  </div>
</template>

